import React, {
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { PanelMenu, PanelMenuItem } from "../PanelMenu";
import { Loading } from "../Loading";
import {
    OverlayTitleBarProps,
    useOverlayTitleBar,
    OverlayViewProps,
    useOverlayView,
} from "modules/common";
import { ReactComponent as ExpandFullScreenIcon } from "assets/icons/expand-fullscreen.svg";
import { ReactComponent as ShrinkFullScreenIcon } from "assets/icons/shrink-fullscreen.svg";

export interface OverlayState {
    previouslySelectedMenuItem?: PanelMenuItem;
    selectedMenuItem?: PanelMenuItem;
    fullScreen?: boolean;
    backButton?: boolean;
    isBackButtonVisible: boolean;
    showLoading: boolean;
    setIsBackButtonVisible: Dispatch<SetStateAction<boolean>>;
    setFullScreen: Dispatch<SetStateAction<boolean>>;
    setShowLoading: Dispatch<SetStateAction<boolean>>;
    changeSelectedMenuItem: (menuItemToChangeTo?: PanelMenuItem) => void;
}

interface Props {
    fullScreenInitially?: boolean;
    searchInFullScreen?: boolean;
    fullScreenButton?: boolean;
    backButton?: boolean;
    containerClass?: string;
    menuItems?: PanelMenuItem[];
    selectedMenuItem?: PanelMenuItem;
    selectFirstMenuItemWhenAvailable?: boolean;
    search?: JSX.Element | null;
    titleBarProps?: OverlayTitleBarProps;
    view?: OverlayViewProps;
    onMenuItemChanged?: (item: PanelMenuItem) => void;
    hideFullScreenButton?: boolean;
}

export const Overlay: React.FC<PropsWithChildren<Props>> = ({
    fullScreenInitially = false,
    searchInFullScreen,
    fullScreenButton,
    backButton,
    containerClass,
    menuItems,
    selectedMenuItem,
    selectFirstMenuItemWhenAvailable,
    search,
    titleBarProps,
    view: viewProps,
    children,
    onMenuItemChanged,
    hideFullScreenButton = false,
}) => {
    const [previouslySelectedMenuItem, setPreviouslySelectedMenuItem] = useState(selectedMenuItem);
    const [selectedMenuItemState, setSelectedMenuItemState] = useState<PanelMenuItem | undefined>(
        selectedMenuItem
    );
    const [fullScreen, setFullScreen] = useState(fullScreenInitially);
    const [showLoading, setShowLoading] = useState(false);
    const [isBackButtonVisible, setIsBackButtonVisible] = useState(false);

    const changeSelectedMenuItem = useCallback(
        (menuItemToChangeTo?: PanelMenuItem) => {
            setPreviouslySelectedMenuItem(selectedMenuItemState);
            setSelectedMenuItemState(menuItemToChangeTo);
            onMenuItemChanged?.(menuItemToChangeTo!);
        },
        [selectedMenuItemState, onMenuItemChanged]
    );

    const state: OverlayState = {
        previouslySelectedMenuItem,
        selectedMenuItem: selectedMenuItemState,
        fullScreen,
        backButton,
        isBackButtonVisible,
        showLoading,
        changeSelectedMenuItem,
        setFullScreen,
        setIsBackButtonVisible,
        setShowLoading,
    };

    useEffect(() => {
        if (!selectFirstMenuItemWhenAvailable || !menuItems) return;
        
        if (!selectedMenuItemState) {
            const itemToSelect = menuItems.find(item => !item.isDisabled);
            if (itemToSelect) {
                setSelectedMenuItemState(itemToSelect);
                return;
            }
            return;
        }
    }, [menuItems, selectFirstMenuItemWhenAvailable, selectedMenuItemState, onMenuItemChanged]);

    const searchElement: JSX.Element | null = useMemo(
        () =>
            search && (!searchInFullScreen || fullScreen) ? (
                <div className="overlay-search">{search}</div>
            ) : null,
        [fullScreen, search, searchInFullScreen]
    );

    const { view, selectedLinkDescription, selectedLinkLabel } = useOverlayView({
        menuItems,
        searchElement,
        state,
        viewProps,
    });

    const { titleBarElement, topPart } = useOverlayTitleBar({
        selectedLinkLabel,
        selectedLinkDescription,
        searchElement,
        state,
        titleBarProps,
    });

    const overlayContainerClasses = useMemo(() => {
        const classes = ["overlay-container"];
        classes.push(titleBarProps?.aboveMenu ? "with-top-row" : "no-top-row");
        if (containerClass) classes.push(containerClass);
        return classes.join(" ");
    }, [containerClass, titleBarProps?.aboveMenu]);

    const fullScreenButtonElement = useMemo(() => {
        if (showLoading || !fullScreenButton || hideFullScreenButton) {
            return null;
        }

        return (
            <button
                className="overlay-full-screen-button"
                onClick={() => setFullScreen(!fullScreen)}
            >
                {fullScreen ? <ShrinkFullScreenIcon /> : <ExpandFullScreenIcon />}
            </button>
        );
    }, [fullScreen, fullScreenButton, showLoading, hideFullScreenButton]);

    return (
        <div className={overlayContainerClasses}>
            {/* If title bar needs to be placed above the menu, reserve a row for it */}
            {titleBarProps?.aboveMenu && (
                <div className="overlay-top-title-bar">{titleBarElement}</div>
            )}

            {/* One third of the screen, only shown when not in full screen */}
            {!fullScreen && (
                <div className="overlay-left">
                    {searchElement}

                    <PanelMenu
                        items={menuItems}
                        selectedItem={selectedMenuItemState}
                        hidden={fullScreen}
                        onItemClicked={item => {
                            const isFullScreen = item.fullScreen ?? false;
                            setFullScreen(isFullScreen);
                            setIsBackButtonVisible(isFullScreen);
                            changeSelectedMenuItem(item);
                            onMenuItemChanged && onMenuItemChanged(item);
                        }}
                    />
                </div>
            )}

            {/* Two thirds on the right or full screen */}
            <div className={`overlay-${fullScreen ? "full-screen" : "right"}`}>
                {topPart}

                {/* Injected view */}
                {view && (
                    <div className="overlay-view-container">
                        {showLoading && <Loading />}

                        {fullScreenButtonElement}

                        {view}
                    </div>
                )}

                {/* Optional - additional children */}
                {children}
            </div>
        </div>
    );
};
